import { useLayoutEffect, useRef, type RefObject } from "react";
import { useIsPresent } from "framer-motion";
import { useTrackContext } from "@/state/TrackContext";
import { bottomSummaryOnBeforeNavigateRef } from "@/shared/bottomSummaryCallbacks";

export interface BottomSummaryNavConfig {
  previousInfo: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
  };
  /** Omit on screens that commit on a single tap (the experience selector and the
      age-group step): they navigate straight away, so a Next button is unreachable. */
  nextInfo?: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    onBeforeNavigate?: () => void;
  };
  mobileNavVariant?: "default" | "experience";
  /** Tile container whose tiles the Next/Checkout pill lines its edges up with on phones. */
  actionAlignRef?: RefObject<HTMLElement>;
}

/**
 * Registers bottom nav for the current screen. Updates synchronously in
 * useLayoutEffect (before paint) so the global BottomSummary does not flicker
 * on route transitions. Pass null on screens without a footer (e.g. home).
 *
 * A screen leaving on a route change keeps its footer registered: the router's
 * AnimatePresence (mode="wait") mounts the next screen only after the exit, so
 * clearing here would blank the whole footer for a frame before the next screen
 * registers its own. Every routed screen registers (a config or null), so the
 * next registration always replaces it.
 */
export function useRegisterBottomSummary(config: BottomSummaryNavConfig | null) {
  const { dispatch } = useTrackContext();
  // False while the owning screen animates out after a route change.
  const isPresent = useIsPresent();
  const isPresentRef = useRef(isPresent);
  isPresentRef.current = isPresent;

  useLayoutEffect(() => {
    bottomSummaryOnBeforeNavigateRef.current = config?.nextInfo?.onBeforeNavigate;

    dispatch({
      type: "SET_BOTTOM_SUMMARY_NAV",
      payload: {
        bottomSummaryNav: config
          ? {
              previousInfo: config.previousInfo,
              nextInfo: config.nextInfo
                ? {
                    pageName: config.nextInfo.pageName,
                    pagePath: config.nextInfo.pagePath,
                    isActive: config.nextInfo.isActive,
                  }
                : undefined,
              mobileNavVariant: config.mobileNavVariant ?? "default",
              actionAlignRef: config.actionAlignRef,
            }
          : null,
      },
    });

    if (config) {
      dispatch({
        type: "SET_NAVIGATION_PREVIOUS_INFO",
        payload: {
          navigationPreviousInfo: {
            pageName: config.previousInfo.pageName,
            pagePath: config.previousInfo.pagePath,
          },
        },
      });
    }

    return () => {
      bottomSummaryOnBeforeNavigateRef.current = undefined;
      if (!isPresentRef.current) return;
      dispatch({
        type: "SET_BOTTOM_SUMMARY_NAV",
        payload: { bottomSummaryNav: null },
      });
    };
    // Granular deps (not `config`) so inline objects from screens do not re-run every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [
    dispatch,
    config?.previousInfo.pageName,
    config?.previousInfo.pagePath,
    config?.previousInfo.isActive,
    config?.nextInfo?.pageName,
    config?.nextInfo?.pagePath,
    config?.nextInfo?.isActive,
    config?.nextInfo?.onBeforeNavigate,
    config?.mobileNavVariant,
    config?.actionAlignRef,
  ]);
}
